import { useContext, useMemo } from 'react';
import get from 'lodash/get';
import {
	useFavoriteBoards,
	useFavoriteDashboards,
	useFavoriteFilters,
	useFavoriteProjects,
	useFavoriteQueues,
	useFavoritePlans,
} from '@atlassian/jira-favorites-store/src/index.tsx';
import type { ItemMap } from '@atlassian/jira-favourite-change-provider/src/model/types.tsx';
import { FavoriteChangeContext } from '@atlassian/jira-favourite-change-provider/src/view/index.tsx';
import type {
	BoardItem,
	DashboardItem,
	FilterItem,
	PlanItem,
	ProjectItem,
	QueueItem,
	RecentItem,
} from '@atlassian/jira-nav-item-service/src/types.tsx';
import { usePlansItems } from '@atlassian/jira-plans-item-store/src/index.tsx';
import {
	useRecentAllSingleList,
	useWorkedOnAllSingleList,
	useRecentBoards,
	useRecentDashboards,
	useRecentFilters,
	useRecentProjects,
	useRecentQueues,
} from '@atlassian/jira-recents-store/src/index.tsx';
import type { UseNavItemsType, UseRecentNavItemsType } from './types.tsx';

type GetFavouriteAndRecentItemsReturnType<T> = {
	favoriteItems: T[];
	recentItems: T[];
};
const getFavouriteAndRecentItems = <
	T extends BoardItem | DashboardItem | FilterItem | ProjectItem | QueueItem | PlanItem,
>(
	recentItems: T[] | null,
	favoriteItems: T[] | null,
	changedItems?: ItemMap,
): GetFavouriteAndRecentItemsReturnType<T> => {
	const mapChangedItems = (item: T) => {
		const changedItem = get(changedItems, item.id);
		return changedItem ? { ...item, favourite: changedItem.value } : item;
	};

	const updatedFavorites = favoriteItems ? favoriteItems.map<T>(mapChangedItems) : [];
	const updatedRecents = recentItems ? recentItems.map<T>(mapChangedItems) : [];
	// this logic relies on the fact that we defer update favorites store on dropdown close
	// so we can assume that favorites list always affects recent list
	const favoritesIds = updatedFavorites.map((item) => item.id);
	const filteredRecents = updatedRecents.filter((item) => !favoritesIds.includes(Number(item.id)));
	return { favoriteItems: updatedFavorites, recentItems: filteredRecents };
};

const getFilteredViewedItems = (
	viewedItems: null | Array<RecentItem>,
	// @ts-expect-error - TS2304 - Cannot find name 'IssueItem'.
	workedOnItems: null | Array<IssueItem>,
): RecentItem[] =>
	viewedItems?.filter(
		(viewedItem) =>
			!workedOnItems?.some(
				// @ts-expect-error - TS2304 - Cannot find name 'IssueItem'.
				(workedOnItem: IssueItem) =>
					parseInt(workedOnItem.localResourceId, 10) === viewedItem.id &&
					workedOnItem.type === viewedItem.type,
			),
	) || [];

export const useBoardNavItems = (): UseNavItemsType<BoardItem> => {
	const {
		changeFavorite,
		items: { boards: changedItems },
	} = useContext(FavoriteChangeContext);
	const [recentData] = useRecentBoards();
	const [favoriteData] = useFavoriteBoards();

	const { favoriteItems, recentItems } = useMemo(
		() => getFavouriteAndRecentItems<BoardItem>(recentData.items, favoriteData.items, changedItems),
		[recentData.items, favoriteData.items, changedItems],
	);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};

export const useDashboardNavItems = (): UseNavItemsType<DashboardItem> => {
	const {
		changeFavorite,
		items: { dashboards: changedItems },
	} = useContext(FavoriteChangeContext);
	const [recentData] = useRecentDashboards();
	const [favoriteData] = useFavoriteDashboards();

	const { favoriteItems, recentItems } = useMemo(
		() =>
			getFavouriteAndRecentItems<DashboardItem>(recentData.items, favoriteData.items, changedItems),
		[recentData.items, favoriteData.items, changedItems],
	);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};

export const useFilterNavItems = (): UseNavItemsType<FilterItem> => {
	const {
		changeFavorite,
		items: { filters: changedItems },
	} = useContext(FavoriteChangeContext);
	const [recentData] = useRecentFilters();
	const [favoriteData] = useFavoriteFilters();

	const { favoriteItems, recentItems } = useMemo(
		() =>
			getFavouriteAndRecentItems<FilterItem>(recentData.items, favoriteData.items, changedItems),
		[recentData.items, favoriteData.items, changedItems],
	);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};

export const usePlanNavItems = (): UseNavItemsType<PlanItem> => {
	const {
		changeFavorite,
		items: { plans: changedItems },
	} = useContext(FavoriteChangeContext);
	const [{ items }] = usePlansItems();
	const [favoriteData] = useFavoritePlans();

	const { favoriteItems, recentItems } = useMemo(() => {
		const recentPlans = items?.recent
			? items.recent.map(
					// the id is a concatenation of planId and scenarioId...
					// we need to strip the scenario so we just have the plan id
					(plan) => ({
						...plan,
						id: plan.id.toString().split('|')[0],
					}),
				)
			: [];
		return getFavouriteAndRecentItems<PlanItem>(recentPlans, favoriteData.items, changedItems);
	}, [items, favoriteData.items, changedItems]);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};
export const useProjectNavItems = (): UseNavItemsType<ProjectItem> => {
	const {
		changeFavorite,
		items: { projects: changedItems },
	} = useContext(FavoriteChangeContext);
	const [recentData] = useRecentProjects();
	const [favoriteData] = useFavoriteProjects();

	const { favoriteItems, recentItems } = useMemo(
		() =>
			getFavouriteAndRecentItems<ProjectItem>(recentData.items, favoriteData.items, changedItems),
		[recentData.items, favoriteData.items, changedItems],
	);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};

export const useRecentNavItems = (): UseRecentNavItemsType => {
	const [viewedData] = useRecentAllSingleList();
	const [workedOnData] = useWorkedOnAllSingleList();

	const viewedItems = useMemo(
		() => getFilteredViewedItems(viewedData.items, workedOnData.items),
		[viewedData.items, workedOnData.items],
	);

	return {
		workedOnItems: workedOnData.items || [],
		viewedItems,
	};
};

export const useQueueNavItems = (): UseNavItemsType<QueueItem> => {
	const {
		changeFavorite,
		items: { queues: changedItems },
	} = useContext(FavoriteChangeContext);
	const [recentData] = useRecentQueues();
	const [favoriteData] = useFavoriteQueues();

	const { favoriteItems, recentItems } = useMemo(
		() => getFavouriteAndRecentItems<QueueItem>(recentData.items, favoriteData.items, changedItems),
		[recentData.items, favoriteData.items, changedItems],
	);

	return {
		changeFavorite,
		// @ts-expect-error - TS2322 - Type 'ItemMap | undefined' is not assignable to type 'ItemMap'.
		changedItems,
		favoriteItems,
		recentItems,
	};
};
